import React from 'react';

import { useBrand } from '@atc/brand';

import { DataTable, Text } from 'reaxl';
import { DelayedImpressionObserver, sendClick, sendImpressions } from 'reaxl-analytics';
import { psxManufactureWarrantyClick, psxManufactureWarrantyImpression } from 'reaxl-analytics-handlers';

import Card from './Card';
import { cardImages } from './staticContent';

const dataTable = (data) => (
    <div className="margin-top-4 margin-bottom-4 margin-bottom-sm-0">
        <DataTable
            data={data}
            className="margin-bottom-0"
        >
            <DataTable.Column
                dataKey="name"
            />
            <DataTable.Column
                className="text-right"
                dataKey="value"
                render={({ row }) => (
                    <>
                        {row.warrantyDuration}
                        {row.remainingWarranty && (
                            <span className="text-size-200 display-block display-sm-inline">
                                &nbsp;
                                {row.remainingWarranty}
                            </span>
                        )}
                        /
                        {row.warrantyMiles}
                        {row.remainingMiles !== 'Unlimited' && (
                            <span className="text-size-200 display-block display-sm-inline">
                                &nbsp;
                                {row.remainingMiles}
                            </span>
                        )}
                    </>
                )}
            />
        </DataTable>
        <Text
            size="200"
            componentClass="div"
            className="padding-top-2"
        >
            * Estimated - Actual manufacturer warranty terms may vary. See the Owner&rsquo;s Manual for warranty coverage, terms, and exclusions.
        </Text>
    </div>
);

const formatRemainingMiles = (miles) => `(${miles.toLocaleString()} miles remain*)`;
const formatRemainingWarranty = (remainingMonths) => {
    const years = Math.floor(remainingMonths / 12);
    const months = remainingMonths % 12;
    if (years > 0) {
        return `(${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''} remain*)`;
    }
    return `(${months} month${months > 1 ? 's' : ''} remain*)`;

};

function PsxWarrantyCard({
    warranty = {},
    listingId,
    ...rest
}) {
    const { brands, isBrand } = useBrand();
    const isAtc = isBrand(brands.ATC_BRAND);

    const handleCardExpansion = (event) => {
        sendClick(psxManufactureWarrantyClick, event, { listingId });
    };

    const handleImpression = () => {
        sendImpressions({ name: psxManufactureWarrantyImpression, data: { listingId } });
    };

    const warrantyData = Object.keys(warranty).map((k) => {
        const warrantyDuration = warranty[k].years + ' Years';
        let warrantyMiles = '';
        let remainingWarranty = '';
        let remainingMiles = '';
        if (warranty[k].miles > 0) {
            warrantyMiles = warranty[k].miles.toLocaleString() + ' Miles';
        }
        if (warranty[k].remainingMiles > 0) {
            remainingMiles = formatRemainingMiles(warranty[k].remainingMiles);
        } else if (warranty[k].remainingMiles === -1) {
            remainingMiles = 'Unlimited';
            warrantyMiles = 'Unlimited Miles (*)';
        }
        if (warranty[k].remainingTotalMonths > 0) {
            remainingWarranty = formatRemainingWarranty(warranty[k].remainingTotalMonths);
        }

        return {
            name: warranty[k].label,
            warrantyDuration,
            remainingWarranty,
            warrantyMiles,
            remainingMiles,
            remainingYears: warranty[k].remainingYears,
        };
    });

    const filteredWarrantyData = warrantyData.filter((data) => data.remainingMiles && (data.remainingYears >= 0 || data.remainingWarranty));

    if (filteredWarrantyData.length === 0) return null;

    return (
        <DelayedImpressionObserver
            onChange={handleImpression}
            key="psx-warranty-card-delayed-impression-observer"
        >
            <Card
                id="psx_warranty"
                heading="Manufacturer Warranty Remaining"
                image={isAtc ? cardImages.WARRANTY_ATC : cardImages.WARRANTY}
                summary="We estimate that this vehicle has manufacturer coverage remaining."
                showTile
                collapsible
                onExpand={handleCardExpansion}
                isInverse
                {...rest}
            >
                {dataTable(filteredWarrantyData)}
            </Card>
        </DelayedImpressionObserver>
    );
}

export default PsxWarrantyCard;
